import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle2, Mail, Phone, MapPin } from 'lucide-react';
import { siteConfig } from '@/siteConfig';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const { language, t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    province: '',
    industry: '',
    voltage: '',
    number_of_io: '',
    preferred_plc_brand: '',
    timeline: '',
    project_description: '',
    honeypot: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    document.title = language === 'fr' ? 'Contact | NgSys Automatisation' : 'Contact | NgSys Automation';
  }, [language]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = language === 'fr' ? 'Nom requis' : 'Name required';
    if (!formData.company.trim()) newErrors.company = language === 'fr' ? 'Entreprise requise' : 'Company required';
    if (!formData.email.trim()) {
      newErrors.email = language === 'fr' ? 'Courriel requis' : 'Email required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = language === 'fr' ? 'Courriel invalide' : 'Invalid email';
    }
    if (!formData.phone.trim()) newErrors.phone = language === 'fr' ? 'Téléphone requis' : 'Phone required';
    if (!formData.province) newErrors.province = language === 'fr' ? 'Province requise' : 'Province required';
    if (!formData.industry) newErrors.industry = language === 'fr' ? 'Secteur requis' : 'Industry required';
    if (!formData.voltage) newErrors.voltage = language === 'fr' ? 'Tension requise' : 'Voltage required';
    if (!formData.project_description.trim()) {
      newErrors.project_description = language === 'fr' ? 'Description requise' : 'Description required';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.honeypot) {
      return;
    }

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/quote-request`, {
        language,
        name: formData.name,
        company: formData.company,
        email: formData.email,
        phone: formData.phone,
        province: formData.province,
        industry: formData.industry,
        voltage: formData.voltage,
        number_of_io: formData.number_of_io || 'Not specified',
        preferred_plc_brand: formData.preferred_plc_brand || 'Not specified',
        timeline: formData.timeline || 'Not specified',
        project_description: formData.project_description,
        chatbot_summary: null,
        source: 'form'
      });

      setIsSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({ submit: language === 'fr' ? 'Erreur lors de la soumission' : 'Submission error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center py-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-md mx-auto text-center px-4"
        >
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="font-heading text-2xl font-bold mb-4">
            {language === 'fr' ? 'Demande soumise avec succès!' : 'Request Submitted Successfully!'}
          </h2>
          <p className="text-muted-foreground mb-8">
            {language === 'fr'
              ? 'Merci pour votre intérêt. Notre équipe vous contactera sous peu pour discuter de votre projet.'
              : 'Thank you for your interest. Our team will contact you shortly to discuss your project.'}
          </p>
          <div className="bg-muted p-4 rounded-lg text-left text-sm space-y-2">
            <p><strong>{language === 'fr' ? 'Nom' : 'Name'}:</strong> {formData.name}</p>
            <p><strong>{language === 'fr' ? 'Entreprise' : 'Company'}:</strong> {formData.company}</p>
            <p><strong>{language === 'fr' ? 'Courriel' : 'Email'}:</strong> {formData.email}</p>
            <p><strong>{language === 'fr' ? 'Secteur' : 'Industry'}:</strong> {formData.industry}</p>
            <p><strong>{language === 'fr' ? 'Tension' : 'Voltage'}:</strong> {formData.voltage}</p>
          </div>
          <Button asChild className="mt-8" size="lg">
            <a href="/">{language === 'fr' ? "Retour à l'accueil" : 'Back to Home'}</a>
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col">
        <section className="py-20 md:py-32 bg-gradient-to-br from-primary/10 via-background to-accent/5">
          <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6" data-testid="contact-title">
              {language === 'fr' ? 'Demander une Soumission' : 'Request a Quote'}
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {language === 'fr' ? 'Remplissez le formulaire ci-dessous et notre équipe vous contactera rapidement.' : 'Fill out the form below and our team will contact you promptly.'}
            </p>
          </div>
        </section>
        <section className="py-20" data-testid="contact-form-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              <div className="space-y-6">
                <Card><CardContent className="p-6"><h3 className="font-heading text-xl font-semibold mb-4">{language === 'fr' ? 'Coordonnées' : 'Contact Information'}</h3><div className="space-y-4"><div className="flex items-start gap-3"><Mail className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /><div><p className="text-sm font-medium">{language === 'fr' ? 'Courriel' : 'Email'}</p><a href={`mailto:${siteConfig.contact.email}`} className="text-sm text-muted-foreground hover:text-primary">{siteConfig.contact.email}</a></div></div><div className="flex items-start gap-3"><Phone className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /><div><p className="text-sm font-medium">{language === 'fr' ? 'Téléphone' : 'Phone'}</p><a href={`tel:${siteConfig.contact.phone}`} className="text-sm text-muted-foreground hover:text-primary">{siteConfig.contact.phone}</a></div></div><div className="flex items-start gap-3"><MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /><div><p className="text-sm font-medium">{language === 'fr' ? 'Localisation' : 'Location'}</p><p className="text-sm text-muted-foreground">{t(siteConfig.contact.address)}</p></div></div></div></CardContent></Card>
                <Card><CardContent className="p-6"><h4 className="font-heading text-lg font-semibold mb-3">{language === 'fr' ? "Heures d'ouverture" : 'Business Hours'}</h4><p className="text-sm text-muted-foreground">{language === 'fr' ? 'Lundi - Vendredi: 8h00 - 17h00' : 'Monday - Friday: 8:00 AM - 5:00 PM'}</p></CardContent></Card>
              </div>
              <div className="lg:col-span-2">
                <Card><CardContent className="p-8"><form onSubmit={handleSubmit} className="space-y-6">
                  <input type="text" name="honeypot" value={formData.honeypot} onChange={(e) => handleChange('honeypot', e.target.value)} style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div><Label htmlFor="name">{language === 'fr' ? 'Nom complet' : 'Full Name'} <span className="text-destructive">*</span></Label><Input id="name" data-testid="contact-name-input" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} placeholder={language === 'fr' ? 'Jean Tremblay' : 'John Smith'} className={errors.name ? 'border-destructive' : ''} />{errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}</div>
                    <div><Label htmlFor="company">{language === 'fr' ? 'Entreprise' : 'Company'} <span className="text-destructive">*</span></Label><Input id="company" data-testid="contact-company-input" value={formData.company} onChange={(e) => handleChange('company', e.target.value)} placeholder={language === 'fr' ? "Nom de l'entreprise" : 'Company Name'} className={errors.company ? 'border-destructive' : ''} />{errors.company && <p className="text-xs text-destructive mt-1">{errors.company}</p>}</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div><Label htmlFor="email">{language === 'fr' ? 'Courriel' : 'Email'} <span className="text-destructive">*</span></Label><Input id="email" type="email" data-testid="contact-email-input" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="example@company.com" className={errors.email ? 'border-destructive' : ''} />{errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}</div>
                    <div><Label htmlFor="phone">{language === 'fr' ? 'Téléphone' : 'Phone'} <span className="text-destructive">*</span></Label><Input id="phone" type="tel" data-testid="contact-phone-input" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder="+1 (514) 555-0000" className={errors.phone ? 'border-destructive' : ''} />{errors.phone && <p className="text-xs text-destructive mt-1">{errors.phone}</p>}</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div><Label htmlFor="province">{language === 'fr' ? 'Province' : 'Province'} <span className="text-destructive">*</span></Label><Select value={formData.province} onValueChange={(value) => handleChange('province', value)}><SelectTrigger id="province" data-testid="contact-province-select" className={errors.province ? 'border-destructive' : ''}><SelectValue placeholder={language === 'fr' ? 'Sélectionner' : 'Select'} /></SelectTrigger><SelectContent>{siteConfig.provinces.map(prov => <SelectItem key={prov} value={prov}>{prov}</SelectItem>)}</SelectContent></Select>{errors.province && <p className="text-xs text-destructive mt-1">{errors.province}</p>}</div>
                    <div><Label htmlFor="industry">{language === 'fr' ? 'Secteur' : 'Industry'} <span className="text-destructive">*</span></Label><Select value={formData.industry} onValueChange={(value) => handleChange('industry', value)}><SelectTrigger id="industry" data-testid="contact-industry-select" className={errors.industry ? 'border-destructive' : ''}><SelectValue placeholder={language === 'fr' ? 'Sélectionner' : 'Select'} /></SelectTrigger><SelectContent>{siteConfig.industries[language].map(ind => <SelectItem key={ind} value={ind}>{ind}</SelectItem>)}</SelectContent></Select>{errors.industry && <p className="text-xs text-destructive mt-1">{errors.industry}</p>}</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div><Label htmlFor="voltage">{language === 'fr' ? 'Tension' : 'Voltage'} <span className="text-destructive">*</span></Label><Select value={formData.voltage} onValueChange={(value) => handleChange('voltage', value)}><SelectTrigger id="voltage" data-testid="contact-voltage-select" className={errors.voltage ? 'border-destructive' : ''}><SelectValue placeholder={language === 'fr' ? 'Sélectionner' : 'Select'} /></SelectTrigger><SelectContent>{siteConfig.voltages.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select>{errors.voltage && <p className="text-xs text-destructive mt-1">{errors.voltage}</p>}</div>
                    <div><Label htmlFor="number_of_io">{language === 'fr' ? "Nombre d'I/O" : 'Number of I/O'}</Label><Input id="number_of_io" data-testid="contact-io-input" value={formData.number_of_io} onChange={(e) => handleChange('number_of_io', e.target.value)} placeholder="ex: 50" /></div>
                    <div><Label htmlFor="preferred_plc_brand">{language === 'fr' ? 'PLC préféré' : 'Preferred PLC'}</Label><Select value={formData.preferred_plc_brand} onValueChange={(value) => handleChange('preferred_plc_brand', value)}><SelectTrigger id="preferred_plc_brand" data-testid="contact-plc-select"><SelectValue placeholder={language === 'fr' ? 'Sélectionner' : 'Select'} /></SelectTrigger><SelectContent>{siteConfig.plcBrands.map(plc => <SelectItem key={plc} value={plc}>{plc}</SelectItem>)}</SelectContent></Select></div>
                  </div>
                  <div><Label htmlFor="timeline">{language === 'fr' ? 'Échéancier' : 'Timeline'}</Label><Select value={formData.timeline} onValueChange={(value) => handleChange('timeline', value)}><SelectTrigger id="timeline" data-testid="contact-timeline-select"><SelectValue placeholder={language === 'fr' ? 'Sélectionner' : 'Select'} /></SelectTrigger><SelectContent>{siteConfig.timelines.map(tl => <SelectItem key={t(tl)} value={t(tl)}>{t(tl)}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label htmlFor="project_description">{language === 'fr' ? 'Description du projet' : 'Project Description'} <span className="text-destructive">*</span></Label><Textarea id="project_description" data-testid="contact-description-textarea" value={formData.project_description} onChange={(e) => handleChange('project_description', e.target.value)} placeholder={language === 'fr' ? 'Décrivez votre projet en détail...' : 'Describe your project in detail...'} rows={5} className={errors.project_description ? 'border-destructive' : ''} />{errors.project_description && <p className="text-xs text-destructive mt-1">{errors.project_description}</p>}</div>
                  {errors.submit && <p className="text-sm text-destructive">{errors.submit}</p>}
                  <Button type="submit" disabled={isSubmitting} size="lg" className="w-full bg-accent hover:bg-accent/90 font-bold uppercase tracking-wide" data-testid="contact-submit-button">{isSubmitting ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" />{language === 'fr' ? 'Envoi en cours...' : 'Submitting...'}</>) : (language === 'fr' ? 'Envoyer la demande' : 'Submit Request')}</Button>
                </form></CardContent></Card>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};